(function (window, document) {
  "use strict";

  var VISITOR_ID_KEY = "cuid";
  var FIRST_SEEN_AT_KEY = "analytics_first_seen_at";
  var SESSION_ID_KEY = "analytics_session_id";
  var SESSION_STARTED_AT_KEY = "analytics_session_started_at";
  var LAST_ACTIVITY_AT_KEY = "analytics_last_activity_at";
  var LAST_PAGE_URL_KEY = "analytics_last_page_url";
  var LANDING_PAGE_URL_KEY = "analytics_landing_page_url";
  var LANDING_REFERRER_KEY = "analytics_landing_referrer";
  var PAGE_SEQUENCE_KEY = "analytics_page_sequence";
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var DEFAULT_ENDPOINT = "/api/analytics/collect";
  var DEFAULT_SITE_ID = "shopify-storefront";
  var SECTION_SELECTOR = "[id*='shopify-section-template']";

  // ─── Cart Attribute 配置 ──────────────────────────────────────────────────
  var CART_ATTRIBUTE_VISITOR_KEY = "_analytics_visitor_id";
  var CART_ATTRIBUTE_SESSION_KEY = "_analytics_session_id";

  function noop() {}

  function safeStorageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safeStorageRemove(storage, key) {
    try {
      storage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function now() {
    return Date.now();
  }

  function toIsoString(timestamp) {
    return new Date(timestamp).toISOString();
  }

  function generateId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return prefix + window.crypto.randomUUID();
    }

    return prefix + Math.random().toString(36).slice(2) + now().toString(36);
  }

  function throttle(fn, delay) {
    var timer = null;

    return function throttled() {
      var context = this;
      var args = arguments;

      if (timer) {
        return;
      }

      timer = window.setTimeout(function () {
        fn.apply(context, args);
        timer = null;
      }, delay);
    };
  }

  function cloneObject(value) {
    return value ? JSON.parse(JSON.stringify(value)) : {};
  }

  function hasNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
  }

  function resolveStringOrDefault(value, fallback) {
    return hasNonEmptyString(value) ? value.trim() : fallback;
  }

  function extractHostname(url) {
    if (!url) {
      return "";
    }

    try {
      return new URL(url, window.location.origin).hostname || "";
    } catch (error) {
      return "";
    }
  }

  function readSearchParams() {
    var params = new URLSearchParams(window.location.search);

    return {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content")
    };
  }

  function getNavigationType() {
    if (!window.performance || typeof window.performance.getEntriesByType !== "function") {
      return "unknown";
    }

    var entries = window.performance.getEntriesByType("navigation");
    if (!entries || !entries.length || !entries[0].type) {
      return "unknown";
    }

    return entries[0].type;
  }

  function buildVisitorState() {
    var visitorId = safeStorageGet(window.localStorage, VISITOR_ID_KEY);
    var firstSeenAt = safeStorageGet(window.localStorage, FIRST_SEEN_AT_KEY);
    var isNewVisitor = false;

    if (!visitorId) {
      visitorId = generateId("v_");
      safeStorageSet(window.localStorage, VISITOR_ID_KEY, visitorId);
      isNewVisitor = true;
    }

    if (!firstSeenAt) {
      firstSeenAt = toIsoString(now());
      safeStorageSet(window.localStorage, FIRST_SEEN_AT_KEY, firstSeenAt);
      isNewVisitor = true;
    }

    return {
      visitorId: visitorId,
      firstSeenAt: firstSeenAt,
      isNewVisitor: isNewVisitor
    };
  }

  function buildSessionState() {
    var currentTimestamp = now();
    var sessionId = safeStorageGet(window.sessionStorage, SESSION_ID_KEY);
    var lastActivityAt = parseInt(
      safeStorageGet(window.sessionStorage, LAST_ACTIVITY_AT_KEY) || "0",
      10
    );
    var sessionStartedAt = safeStorageGet(window.sessionStorage, SESSION_STARTED_AT_KEY);

    if (!sessionId || !lastActivityAt || currentTimestamp - lastActivityAt > SESSION_TIMEOUT_MS) {
      sessionId = generateId("s_");
      sessionStartedAt = toIsoString(currentTimestamp);
      safeStorageSet(window.sessionStorage, SESSION_ID_KEY, sessionId);
      safeStorageSet(window.sessionStorage, SESSION_STARTED_AT_KEY, sessionStartedAt);
      safeStorageSet(window.sessionStorage, PAGE_SEQUENCE_KEY, "0");
      safeStorageRemove(window.sessionStorage, LAST_PAGE_URL_KEY);
      safeStorageRemove(window.sessionStorage, LANDING_PAGE_URL_KEY);
      safeStorageRemove(window.sessionStorage, LANDING_REFERRER_KEY);
    }

    safeStorageSet(window.sessionStorage, LAST_ACTIVITY_AT_KEY, String(currentTimestamp));

    return {
      sessionId: sessionId,
      sessionStartedAt: sessionStartedAt
    };
  }

  function buildPageState() {
    var currentUrl = window.location.href;
    var currentReferrer = document.referrer || "";
    var previousPageUrl = safeStorageGet(window.sessionStorage, LAST_PAGE_URL_KEY) || "";
    var landingPageUrl = safeStorageGet(window.sessionStorage, LANDING_PAGE_URL_KEY) || "";
    var landingReferrer = safeStorageGet(window.sessionStorage, LANDING_REFERRER_KEY) || "";
    var rawSequence = parseInt(safeStorageGet(window.sessionStorage, PAGE_SEQUENCE_KEY) || "0", 10);
    var pageSequence = rawSequence > 0 ? rawSequence : 0;

    if (!previousPageUrl) {
      pageSequence = 1;
    } else if (previousPageUrl !== currentUrl) {
      pageSequence += 1;
    } else if (!pageSequence) {
      pageSequence = 1;
    }

    safeStorageSet(window.sessionStorage, PAGE_SEQUENCE_KEY, String(pageSequence));
    safeStorageSet(window.sessionStorage, LAST_PAGE_URL_KEY, currentUrl);

    if (!landingPageUrl) {
      landingPageUrl = currentUrl;
      safeStorageSet(window.sessionStorage, LANDING_PAGE_URL_KEY, landingPageUrl);
    }

    if (!landingReferrer) {
      landingReferrer = currentReferrer;
      safeStorageSet(window.sessionStorage, LANDING_REFERRER_KEY, landingReferrer);
    }

    return {
      url: currentUrl,
      path: window.location.pathname + window.location.search + window.location.hash,
      title: document.title || "",
      documentReferrer: currentReferrer,
      documentReferrerHost: extractHostname(currentReferrer),
      previousPageUrl: previousPageUrl || "",
      previousPageHost: extractHostname(previousPageUrl),
      landingPageUrl: landingPageUrl,
      landingPageHost: extractHostname(landingPageUrl),
      landingReferrer: landingReferrer,
      landingReferrerHost: extractHostname(landingReferrer),
      pageSequence: pageSequence,
      navigationType: getNavigationType()
    };
  }

  function getBaseContext() {
    return {
      language: window.navigator.language || "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      screen_width: window.screen && window.screen.width ? window.screen.width : null,
      screen_height: window.screen && window.screen.height ? window.screen.height : null,
      viewport_width: window.innerWidth || null,
      viewport_height: window.innerHeight || null,
      user_agent: window.navigator.userAgent || "",
      utm: readSearchParams()
    };
  }

  // ─── Config ──────────────────────────────────────────────────────────────────

  var config = {
    endpoint: resolveStringOrDefault(window.__ANALYTICS_ENDPOINT__, DEFAULT_ENDPOINT),
    siteId: resolveStringOrDefault(window.__ANALYTICS_SITE_ID__, DEFAULT_SITE_ID),
    cartVisitorAttributeKey: resolveStringOrDefault(
      window.__ANALYTICS_CART_ATTRIBUTE_KEY__,
      CART_ATTRIBUTE_VISITOR_KEY
    ),
    cartSessionAttributeKey: resolveStringOrDefault(
      window.__ANALYTICS_CART_SESSION_KEY__,
      CART_ATTRIBUTE_SESSION_KEY
    ),
    fetchOptions: {}
  };

  // ─── State ───────────────────────────────────────────────────────────────────

  var visitorState = buildVisitorState();
  var sessionState = buildSessionState();
  var pageState = buildPageState();

  function refreshSessionActivity() {
    sessionState = buildSessionState();
    return sessionState;
  }

  // ─── Payload Builder ─────────────────────────────────────────────────────────

  function buildPayload(eventName, properties) {
    refreshSessionActivity();

    return {
      event: eventName,
      event_id: generateId("evt_"),
      site_id: config.siteId,
      client_time: toIsoString(now()),
      visitor_id: visitorState.visitorId,
      session_id: sessionState.sessionId,
      page: {
        url: pageState.url,
        full_url: pageState.url,
        path: pageState.path,
        title: pageState.title,
        document_referrer: pageState.documentReferrer,
        document_referrer_host: pageState.documentReferrerHost,
        previous_page_url: pageState.previousPageUrl,
        previous_page_host: pageState.previousPageHost,
        landing_page_url: pageState.landingPageUrl,
        landing_page_host: pageState.landingPageHost,
        landing_referrer: pageState.landingReferrer,
        landing_referrer_host: pageState.landingReferrerHost,
        page_sequence: pageState.pageSequence,
        navigation_type: pageState.navigationType
      },
      source: {
        current_url: pageState.url,
        referrer_url: pageState.documentReferrer,
        referrer_host: pageState.documentReferrerHost,
        landing_page_url: pageState.landingPageUrl,
        landing_referrer_url: pageState.landingReferrer,
        landing_referrer_host: pageState.landingReferrerHost
      },
      visitor: {
        first_seen_at: visitorState.firstSeenAt,
        is_new_visitor: visitorState.isNewVisitor
      },
      context: getBaseContext(),
      properties: properties || {}
    };
  }

  // ─── Transport ───────────────────────────────────────────────────────────────

  function sendWithFetch(payload, options) {
    var requestOptions = options || {};
    var baseHeaders = (config.fetchOptions && config.fetchOptions.headers) || {};
    var requestHeaders = requestOptions.headers || {};
    var headers = Object.assign({}, baseHeaders, requestHeaders);
    var fetchOptions = Object.assign({}, config.fetchOptions, requestOptions, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
      keepalive: !!requestOptions.keepalive
    });

    return window.fetch(config.endpoint, fetchOptions);
  }

  function sendWithBeacon(payload) {
    if (!window.navigator || typeof window.navigator.sendBeacon !== "function") {
      return false;
    }

    var blob = new window.Blob([JSON.stringify(payload)], {
      type: "application/json"
    });

    return window.navigator.sendBeacon(config.endpoint, blob);
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  var api = {
    configure: function configure(nextConfig) {
      config = Object.assign({}, config, nextConfig || {});
      config.endpoint = resolveStringOrDefault(config.endpoint, DEFAULT_ENDPOINT);
      config.siteId = resolveStringOrDefault(config.siteId, DEFAULT_SITE_ID);
      config.cartVisitorAttributeKey = resolveStringOrDefault(
        config.cartVisitorAttributeKey,
        CART_ATTRIBUTE_VISITOR_KEY
      );
      config.cartSessionAttributeKey = resolveStringOrDefault(
        config.cartSessionAttributeKey,
        CART_ATTRIBUTE_SESSION_KEY
      );
      return cloneObject(config);
    },

    getConfig: function getConfig() {
      return cloneObject(config);
    },

    getVisitorState: function getVisitorState() {
      return cloneObject(visitorState);
    },

    getSessionState: function getSessionState() {
      return cloneObject(sessionState);
    },

    getPageState: function getPageState() {
      return cloneObject(pageState);
    },

    getVisitorId: function getVisitorId() {
      return visitorState.visitorId;
    },

    getSessionId: function getSessionId() {
      refreshSessionActivity();
      return sessionState.sessionId;
    },

    generateId: generateId,

    throttle: throttle,

    track: function track(eventName, properties, options) {
      var payload = buildPayload(eventName, properties);
      var trackOptions = options || {};

      if (trackOptions.transport === "beacon") {
        if (sendWithBeacon(payload)) {
          return Promise.resolve({ ok: true, transport: "beacon" });
        }

        return sendWithFetch(payload, { keepalive: true });
      }

      return sendWithFetch(payload, trackOptions);
    }
  };

  window.SiteAnalytics = api;

  // ─── Page View Tracking ──────────────────────────────────────────────────────

  function trackPageView() {
    api
      .track("page_view", {
        page_url: pageState.url,
        full_url: pageState.url,
        page_title: pageState.title,
        page_path: pageState.path,
        document_referrer: pageState.documentReferrer || null,
        document_referrer_host: pageState.documentReferrerHost || null,
        previous_page_url: pageState.previousPageUrl || null,
        previous_page_host: pageState.previousPageHost || null,
        landing_page_url: pageState.landingPageUrl || null,
        landing_referrer: pageState.landingReferrer || null,
        landing_referrer_host: pageState.landingReferrerHost || null,
        page_sequence: pageState.pageSequence,
        navigation_type: pageState.navigationType,
        is_new_visitor: visitorState.isNewVisitor
      })
      .catch(noop);
  }

  // ─── Scroll & Section Engagement Tracking ────────────────────────────────────

  function getScrollPercentage() {
    var doc = document.documentElement;
    var scrollableHeight = Math.max(doc.scrollHeight - doc.clientHeight, 0);

    if (!scrollableHeight) {
      return 0;
    }

    var scrollTop = window.pageYOffset || doc.scrollTop || 0;
    return Math.min(100, Math.max(0, Math.round((scrollTop / scrollableHeight) * 100)));
  }

  function extractShopifySectionName(sectionId) {
    var firstStep = sectionId && sectionId.match(/__(.+)/);
    if (!firstStep || !firstStep[1]) {
      return sectionId || "anonymous_section";
    }

    var match = firstStep[1].match(/(.+)_\w+$/);
    return match ? match[1] : firstStep[1];
  }

  function getSectionName(sectionId, sectionNameCounts) {
    var sectionName = extractShopifySectionName(sectionId);

    if (sectionNameCounts[sectionName] === undefined) {
      sectionNameCounts[sectionName] = 0;
      return sectionName;
    }

    sectionNameCounts[sectionName] += 1;
    return sectionName + "_" + sectionNameCounts[sectionName];
  }

  function hasMeaningfulSnapshot(snapshot) {
    return (
      snapshot.max_scroll_percentage > 0 ||
      snapshot.sections.some(function (section) {
        return section.visible_ms > 0 || section.view_count > 0;
      })
    );
  }

  function SectionTracker() {
    this.maxScrollPercentage = 0;
    this.engagementId = generateId("eng_");
    this.snapshotIndex = 0;
    this.sections = new Map();
    this.lastFlushFingerprint = "";
    this.observer = null;
    this._finalFlushed = false;
    this.handleScroll = throttle(this.updateMaxScroll.bind(this), 200);
    this.init();
  }

  SectionTracker.prototype.init = function init() {
    if (typeof window.IntersectionObserver === "function") {
      this.observer = new window.IntersectionObserver(this.handleIntersection.bind(this), {
        root: null,
        threshold: 0,
        rootMargin: "-20% 0px -30% 0px"
      });
    }

    var tracker = this;
    var sectionNameCounts = {};
    var elements = document.querySelectorAll(SECTION_SELECTOR);

    Array.prototype.forEach.call(elements, function (element) {
      var key = element.id;
      if (!key) {
        return;
      }

      element.setAttribute("data-analytics-key", key);
      tracker.sections.set(key, {
        key: key,
        name: getSectionName(key, sectionNameCounts),
        totalDurationMs: 0,
        currentStartTime: null,
        viewCount: 0
      });

      if (tracker.observer) {
        tracker.observer.observe(element);
      }
    });

    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleScroll);
    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this));
    window.addEventListener("pagehide", this.handlePageHide.bind(this));
    this.intervalId = window.setInterval(this.flush.bind(this, "interval", false), 60000);
  };

  SectionTracker.prototype.updateMaxScroll = function updateMaxScroll() {
    this.maxScrollPercentage = Math.max(this.maxScrollPercentage, getScrollPercentage());
  };

  SectionTracker.prototype.handleIntersection = function handleIntersection(entries) {
    var timestamp = now();
    var hasIntersectionChange = false;

    entries.forEach(
      function (entry) {
        var key = entry.target.getAttribute("data-analytics-key") || entry.target.id || "";
        var section = this.sections.get(key);

        if (!section) {
          return;
        }

        if (entry.isIntersecting) {
          if (section.currentStartTime === null) {
            section.currentStartTime = timestamp;
            section.viewCount += 1;
            hasIntersectionChange = true;
          }
          return;
        }

        if (section.currentStartTime !== null) {
          section.totalDurationMs += timestamp - section.currentStartTime;
          section.currentStartTime = null;
          hasIntersectionChange = true;
        }
      }.bind(this)
    );
  };

  SectionTracker.prototype.pauseActiveSections = function pauseActiveSections() {
    var timestamp = now();

    this.sections.forEach(function (section) {
      if (section.currentStartTime === null) {
        return;
      }

      section.totalDurationMs += timestamp - section.currentStartTime;
      section.currentStartTime = null;
    });
  };

  SectionTracker.prototype.buildSnapshot = function buildSnapshot(reason, isFinal) {
    var timestamp = now();
    var sections = [];

    this.sections.forEach(function (section) {
      var durationMs = section.totalDurationMs;

      if (section.currentStartTime !== null) {
        durationMs += timestamp - section.currentStartTime;
      }

      sections.push({
        section_id: section.key,
        section_name: section.name,
        visible_ms: durationMs,
        view_count: section.viewCount
      });
    });

    return {
      engagement_id: this.engagementId,
      snapshot_index: this.snapshotIndex + 1,
      flush_reason: reason,
      is_final: isFinal,
      max_scroll_percentage: this.maxScrollPercentage,
      sections: sections
    };
  };

  SectionTracker.prototype.flush = function flush(reason, isFinal) {
    var snapshot = this.buildSnapshot(reason, isFinal);
    var fingerprint = JSON.stringify(snapshot);

    if (
      !isFinal &&
      (!hasMeaningfulSnapshot(snapshot) || fingerprint === this.lastFlushFingerprint)
    ) {
      return;
    }

    this.snapshotIndex += 1;
    this.lastFlushFingerprint = fingerprint;

    api
      .track("page_engagement", snapshot, {
        transport: "beacon",
        keepalive: isFinal
      })
      .catch(noop);
  };

  SectionTracker.prototype.handleVisibilityChange = function handleVisibilityChange() {
    if (document.visibilityState !== "hidden") {
      return;
    }

    this.pauseActiveSections();
    this._finalFlushed = true;
    this.flush("hidden", true);
  };

  SectionTracker.prototype.handlePageHide = function handlePageHide() {
    if (this._finalFlushed) return;
    this.pauseActiveSections();
    this.flush("pagehide", true);
  };

  // ─── Cart Tracking Utilities ─────────────────────────────────────────────────

  function resolveRequestPath(resource) {
    try {
      if (typeof resource === "string") {
        return new URL(resource, window.location.origin).pathname.replace(/\/+$/, "");
      }

      if (resource && typeof resource.url === "string") {
        return new URL(resource.url, window.location.origin).pathname.replace(/\/+$/, "");
      }
    } catch (error) {
      return "";
    }

    return "";
  }

  function isCartAddPath(pathname) {
    return pathname === "/cart/add" || pathname === "/cart/add.js";
  }

  function isCartChangePath(pathname) {
    return pathname === "/cart/change" || pathname === "/cart/change.js";
  }

  function isCartMutationPath(pathname) {
    return (
      pathname === "/cart/add" ||
      pathname === "/cart/add.js" ||
      pathname === "/cart/change" ||
      pathname === "/cart/change.js" ||
      pathname === "/cart/update" ||
      pathname === "/cart/update.js" ||
      pathname === "/cart/clear" ||
      pathname === "/cart/clear.js"
    );
  }

  function parseJsonSafely(response) {
    return response
      .clone()
      .json()
      .catch(function () {
        return null;
      });
  }

  function normalizeCartItem(item) {
    if (!item) {
      return null;
    }

    return {
      product_id: item.product_id || null,
      variant_id: item.variant_id || item.id || null,
      sku: item.sku || null,
      title: item.title || item.product_title || null,
      quantity: typeof item.quantity === "number" ? item.quantity : Number(item.quantity || 0),
      price: item.price || null
    };
  }

  function normalizeCartItems(items) {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(normalizeCartItem).filter(function (item) {
      return item && item.quantity > 0;
    });
  }

  function trackCartAdd(data) {
    var item = normalizeCartItem(data);
    if (!item || item.quantity <= 0) {
      return;
    }

    api
      .track("cart_item_added", {
        items: [item]
      })
      .catch(noop);
  }

  function trackCartChange(data) {
    var addedItems = normalizeCartItems(data && data.items_added);
    var removedItems = normalizeCartItems(data && data.items_removed);

    if (addedItems.length) {
      api
        .track("cart_items_added", {
          items: addedItems
        })
        .catch(noop);
    }

    if (removedItems.length) {
      api
        .track("cart_items_removed", {
          items: removedItems
        })
        .catch(noop);
    }
  }

  function getAnalyticsAttributes() {
    var attributes = {};
    attributes[config.cartVisitorAttributeKey] = visitorState.visitorId;
    attributes[config.cartSessionAttributeKey] = sessionState.sessionId;
    return attributes;
  }

  function needsCartAttributeUpdate(cartData) {
    var attrs = cartData && cartData.attributes;
    if (!attrs) return true;

    return (
      attrs[config.cartVisitorAttributeKey] !== visitorState.visitorId ||
      attrs[config.cartSessionAttributeKey] !== sessionState.sessionId
    );
  }

  // ─── 将 analytics attributes 合并进 cart/add 请求的 body ─────────────────────
  // 支持 JSON string 和 FormData 两种格式，其他格式原样透传不做处理
  function mergeCartAddAttributes(fetchConfig, attributes) {
    var cfg = Object.assign({}, fetchConfig);
    var body = cfg.body;

    // JSON 格式
    if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        parsed.attributes = Object.assign({}, parsed.attributes, attributes);
        cfg.body = JSON.stringify(parsed);
        return cfg;
      } catch (e) {
        // body 不是合法 JSON，原样透传
        return cfg;
      }
    }

    // FormData 格式
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var fd = new FormData();
      body.forEach(function (value, key) {
        fd.append(key, value);
      });
      Object.keys(attributes).forEach(function (key) {
        fd.append("attributes[" + key + "]", attributes[key]);
      });
      cfg.body = fd;
      return cfg;
    }

    // URLSearchParams 格式（部分老主题使用）
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      var params = new URLSearchParams(body.toString());
      Object.keys(attributes).forEach(function (key) {
        params.set("attributes[" + key + "]", attributes[key]);
      });
      cfg.body = params;
      return cfg;
    }

    // 其他格式（Blob、ArrayBuffer 等）原样透传，不处理
    return cfg;
  }

  // ─── ensureCartAttributes：用于 cart/change、cart/clear 等非 add 变更后的兜底同步 ──
  var _cartAttrUpdateInFlight = false;

  function ensureCartAttributes() {
    if (_cartAttrUpdateInFlight) {
      return Promise.resolve(null);
    }

    _cartAttrUpdateInFlight = true;

    return window
      .fetch("/cart.js", {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cartData) {
        if (!cartData || !needsCartAttributeUpdate(cartData)) {
          _cartAttrUpdateInFlight = false;
          return cartData;
        }

        // 仅在购物车非空时写入，空购物车 Shopify 不持久化 attributes
        if (!cartData.item_count || cartData.item_count <= 0) {
          _cartAttrUpdateInFlight = false;
          return cartData;
        }

        return window
          .fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: getAnalyticsAttributes() })
          })
          .then(function (response) {
            _cartAttrUpdateInFlight = false;
            return response.ok ? response.json() : null;
          });
      })
      .catch(function () {
        _cartAttrUpdateInFlight = false;
        return null;
      });
  }

  // ─── beaconCartAttributes：页面卸载时兜底同步 ────────────────────────────────
  function beaconCartAttributes() {
    if (!window.navigator || typeof window.navigator.sendBeacon !== "function") {
      return false;
    }

    var blob = new window.Blob(
      [JSON.stringify({ attributes: getAnalyticsAttributes() })],
      { type: "application/json" }
    );

    return window.navigator.sendBeacon("/cart/update.js", blob);
  }

  // ─── Cart Fetch Hook ──────────────────────────────────────────────────────────

  function installCartFetchHook() {
    if (typeof window.fetch !== "function" || window.__siteAnalyticsCartHookInstalled) {
      return;
    }

    window.__siteAnalyticsCartHookInstalled = true;
    var originalFetch = window.fetch.bind(window);

    window.fetch = function patchedFetch(resource, fetchConfig) {
      var pathname = resolveRequestPath(resource);

      // ── cart/add：将 analytics attributes 直接合并进请求 body，零额外请求 ──
      if (isCartAddPath(pathname)) {
        var mergedConfig = mergeCartAddAttributes(fetchConfig || {}, getAnalyticsAttributes());
        return originalFetch(resource, mergedConfig).then(function (response) {
          if (response && response.ok) {
            parseJsonSafely(response).then(function (data) {
              if (data) trackCartAdd(data);
            });
          }
          return response;
        });
      }

      return originalFetch(resource, fetchConfig).then(function (response) {
        if (!pathname || !response || !response.ok) {
          return response;
        }

        // ── cart/change：追踪变更事件，并通过 update.js 兜底同步 attributes ──
        if (isCartChangePath(pathname)) {
          parseJsonSafely(response).then(function (data) {
            if (data) trackCartChange(data);
          });
          window.setTimeout(function () {
            ensureCartAttributes();
          }, 100);
        }

        // ── cart/clear：购物车清空后 attributes 会丢失，下次 add 时会重新注入，无需处理 ──

        return response;
      });
    };
  }

  // ─── Cart Form Hook（表单提交走 /cart/add，无法拦截 body，beacon 兜底）────────
  function installCartFormHook() {
    if (window.__siteAnalyticsFormHookInstalled) {
      return;
    }

    window.__siteAnalyticsFormHookInstalled = true;

    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== "FORM") return;

        var action = form.getAttribute("action") || "";

        if (action.indexOf("/checkout") !== -1 || action.indexOf("/cart") !== -1) {
          beaconCartAttributes();
        }
      },
      true
    );
  }

  function installPageHideHook() {
    window.addEventListener("pagehide", function () {
      beaconCartAttributes();
    });

    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") {
        beaconCartAttributes();
      }
    });
  }

  function initDomTracking() {
    trackPageView();
    new SectionTracker();
  }

  installCartFetchHook();
  installCartFormHook();
  installPageHideHook();

  // 页面加载时仅在购物车非空时同步一次 attributes（回头客场景：购物车已有商品）
  window
    .fetch("/cart.js", {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    })
    .then(function (response) {
      return response.ok ? response.json() : null;
    })
    .then(function (cartData) {
      if (cartData && cartData.item_count > 0) {
        ensureCartAttributes();
      }
    })
    .catch(noop);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDomTracking, { once: true });
  } else {
    initDomTracking();
  }
})(window, document);
